import { html } from 'lit';
import { sectionHeading } from '../components/sectionHeading.js';
import type { LandingContent } from '../../shared/content/landingContent.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';
import { pathJourney } from './pathJourney.js';

// Fallback icons keep the journey readable even when a step has no explicit icon (e.g. legacy locales).
const fallbackStepIcons = [
  'icon-[material-symbols--fact-check]',
  'icon-[material-symbols--manage-search]',
  'icon-[material-symbols--translate]',
  'icon-[material-symbols--approval-delegation]',
  'icon-[material-symbols--flight-takeoff]',
  'icon-[material-symbols--handshake]',
];

const stepIconClass = (iconClass: string | undefined, index: number) =>
  iconClass && iconClass.trim() ? iconClass : (fallbackStepIcons[index % fallbackStepIcons.length] as string);

const processStepDiagrams: Record<LandingContent['locale'], string[][]> = {
  de: [
    ['Rolle', 'Anforderungen', 'Zeitplan'],
    ['Motivation', 'Qualifikation', 'Passung'],
    ['Sprache', 'Unterlagen', 'Interview'],
    ['Anerkennung', 'Visum', 'Freigabe'],
    ['Einreise', 'Unterkunft', 'Arbeitsstart'],
    ['Feedback', 'Integration', 'Bindung'],
  ],
  tr: [
    ['Pozisyon', 'Koşullar', 'Takvim'],
    ['Motivasyon', 'Yeterlilik', 'Uyum'],
    ['Dil', 'Belgeler', 'Görüşme'],
    ['Denklik', 'Vize', 'Onay'],
    ['Giriş', 'Konaklama', 'İşe başlangıç'],
    ['Geri bildirim', 'Uyum', 'Bağlılık'],
  ],
  en: [
    ['Role', 'Requirements', 'Timeline'],
    ['Motivation', 'Qualification', 'Fit'],
    ['Language', 'Documents', 'Interview'],
    ['Recognition', 'Visa', 'Approval'],
    ['Arrival', 'Housing', 'Work start'],
    ['Feedback', 'Integration', 'Retention'],
  ],
};

export const processSection = (content: LandingContent, editor?: LandingEditorRenderOptions) => html`
  <section
    id="ablauf"
    class=${editorNodeClass(editor, 'section.process', 'vp-section vp-process-section')}
    style=${editorNodeStyle(editor, 'section.process')}
    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'section.process', event)}
    @click=${(event: MouseEvent) => editorNodeClick(editor, 'section.process', event)}
  >
    <div class="vp-shell">
      ${sectionHeading({
        eyebrow: content.process.eyebrow,
        title: content.process.title,
        highlight: content.process.titleHighlight,
        text: content.process.text,
        align: 'center',
        editor,
        elementPrefix: 'process',
      })}
      ${pathJourney({
        items: content.process.steps.map((step, index) => ({
          iconClass: stepIconClass(step.iconClass, index),
          title: step.title,
          text: step.text,
          image: step.image,
          imageAlt: step.imageAlt,
          diagram: processStepDiagrams[content.locale][index],
        })),
        ids: {
          card: index => `process.step.${index}`,
          icon: index => `process.step.${index}.icon`,
          title: index => `process.step.${index}.title`,
          text: index => `process.step.${index}.text`,
          image: index => `process.step.${index}.image`,
        },
        containerId: 'process.steps.container',
        editor,
      })}
    </div>
  </section>
`;
